import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";
import { AuthenticationMethodsType } from "@atlassiansox/asc-stargate";

import { getGrsDataFromLocalStorage } from "@/utils/anchor-queries/get-bandits-contents";

export const CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES = {
    PAGE_LOADED: "check_email_page_loaded",
    OPEN_GMAIL_CLICKED: "check_email_page_open_gmail_clicked",
    OPEN_OUTLOOK_CLICKED: "check_email_page_open_outlook_clicked",
    CONTACT_LINK_CLICKED: "check_email_page_contact_link_clicked",
    RESEND_EMAIL_CLICKED: "check_email_page_resend_email_clicked",
    RESEND_EMAIL_API_SUCCESS: "check_email_page_resend_email_api_success",
    RESEND_EMAIL_API_FAILURE: "check_email_page_resend_email_api_failure"
};

/**
 * Tracks Continue with Social SSO button clicks
 * @param AnalyticsEventType
 * @param type
 * @param productKey
 * @param edition
 */
export function trackContinueWithSocialSignups(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    type: string,
    productKey?: string,
    edition?: string
) {
    const ajcbGrsData = getGrsDataFromLocalStorage();
    const recoSessionId = ajcbGrsData?.recoSessionId;
    const recoEntityId = ajcbGrsData?.recoEntityId;

    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: `wac-signup-${type}`
    }).fire("signup");

    // DATA FOUNDATIONS TRACKING
    createAnalyticsEvent({
        eventType: "track",
        action: "started",
        actionSubject: "accountCreation",
        containerId: "bxp-ui-signup",
        actionSubjectId: "social",
        attributes: {
            socialProvider: type,
            productKey,
            edition,
            recoSessionId,
            recoEntityId
        }
    }).fire();
}

type DataFoundationsAccountCreationEventPayload = {
    actionSubjectId?: "standalone" | "embedded";
    productKey?: string;
    edition?: string;
};
/**
 * Account Creation Component Viewed
 * @param createAnalyticsEvent
 * @param payload
 */
export function trackDataFoundationsAccountCreationComponent(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    payload: DataFoundationsAccountCreationEventPayload
) {
    // DATA FOUNDATIONS TRACKING
    const { actionSubjectId, productKey, edition } = payload;
    createAnalyticsEvent({
        eventType: "ui",
        action: "viewed",
        actionSubject: "accountCreationComponent",
        containerId: "bxp-ui-signup",
        actionSubjectId,
        attributes: {
            productKey,
            edition
        }
    }).fire();
}

/**
 * Tracks submit interaction
 * @param AnalyticsEventType
 * @param productKey
 * @param edition
 */
export function trackFormSubmit(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    productKey?: string,
    edition?: string
) {
    const ajcbGrsData = getGrsDataFromLocalStorage();
    const recoSessionId = ajcbGrsData?.recoSessionId;
    const recoEntityId = ajcbGrsData?.recoEntityId;

    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: "bxp.formCloudSignup.submitWithCaptcha"
    }).fire("signup");
    createAnalyticsEvent({
        data: {
            type: "N2N"
        },
        event: "clicked",
        eventComponent: "button",
        eventName: "bxp.formCloudSignup.formSubmit"
    }).fire("signup");

    // DATA FOUNDATIONS TRACKING
    createAnalyticsEvent({
        eventType: "track",
        action: "started",
        actionSubject: "accountCreation",
        containerId: "bxp-ui-form",
        actionSubjectId: "email",
        attributes: {
            productKey,
            edition,
            recoSessionId,
            recoEntityId
        }
    }).fire();
}

/**
 * Tracks recaptcha failure
 * @param AnalyticsEventType
 */
export function trackRecaptchaFailure(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    eventColumn?: string
) {
    createAnalyticsEvent({
        data: {
            event: "submitFailed",
            uuid: "reactSignUpForm"
        },
        event: "clicked",
        eventComponent: "button",
        eventColumn,
        eventName: "bxp.formCloudSignup.submitFailureRecaptcha"
    }).fire("signup");
}

/**
 * Tracks submit failure due to invalid form (empty or invalid email value)
 * @param AnalyticsEventType
 */
export function trackInvalidEmail(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        data: {
            event: "submitFailed",
            uuid: "reactSignUpForm"
        },
        event: "clicked",
        eventComponent: "button",
        eventName: "bxp.formCloudSignup.submitFailureInvalidEmail"
    }).fire("signup");
}

/**
 * Tracks submit success
 * @param AnalyticsEventType
 */
export function trackSubmitSuccess(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        data: {
            event: "submitSucceeded",
            uuid: "reactSignUpForm"
        },
        event: "clicked",
        eventComponent: "button",
        eventName: "reactSignUpFormSubmitSucceeded"
    }).fire("signup");
}

/**
 * Tracks signup submit failure
 * @param AnalyticsEventType
 */
export function trackSignupFailure(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        data: {
            event: "submitFailed",
            uuid: "reactSignUpForm"
        },
        event: "clicked",
        eventComponent: "button",
        eventName: "reactSignUpFormSubmitFailed"
    }).fire("signup");
}

/**
 * Tracks sign up with email submit button click
 * @param AnalyticsEventType
 */
export function trackSubmitButtonClick(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: "wac-try-signup"
    }).fire("signup");
}

/**
 * Tracks signup form load event
 * @param AnalyticsEventType
 */
export function trackSignFormLoad(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        event: "loaded",
        eventColumn: "signUp",
        eventComponent: "signUp",
        eventName: "signUpLoaded",
        eventType: "ui"
    }).fire("signup");
}

/**
 * Tracks time to form ready
 * @param AnalyticsEventType
 */
export function trackTimeToFormReady(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    performanceStartTime: number
) {
    createAnalyticsEvent({
        eventName: "bxp.formCloudSignup.timeToFormReady",
        performanceDuration: performance.now() - performanceStartTime,
        isLoggedIn: false
    }).fire("signup");
}

/**
 * Tracks existing user redirects to type on user signup
 * @param AnalyticsEventType
 */
export function trackExistingUserRedirectTo(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    type: AuthenticationMethodsType
) {
    createAnalyticsEvent({
        eventName: "reactSignUpFormSubmitExistingUserRedirect",
        eventComponent: "button",
        event: "clicked",
        data: {
            event: `submitRedirectTo${type}`,
            uuid: "reactSignUpForm"
        }
    }).fire("signup");
}

type DataFoundationsSiteNameEventPayload = {
    actionSubjectId?: "standalone" | "embedded";
    cohort?: string;
    productKey?: string;
    edition?: string;
    userType?: string;
    domainType?: string;
    companyType?: string;
    numTxas?: number;
};
/**
 * Tracks when the Site Activation screen is viewed
 * @param createAnalyticsEvent
 * @param payload
 *
 */
export function trackSiteActivationViewed(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    {
        actionSubjectId,
        cohort,
        productKey,
        edition,
        userType,
        domainType,
        companyType,
        numTxas
    }: DataFoundationsSiteNameEventPayload
) {
    // DATA FOUNDATIONS TRACKING
    createAnalyticsEvent({
        eventType: "ui",
        action: "viewed",
        actionSubject: "siteActivationComponent",
        actionSubjectId,
        attributes: {
            cohort,
            productKey,
            edition,
            userType,
            domainType,
            companyType,
            numTxas
        }
    }).fire();
}

/**
 * Tracks when we start provisioning a site.
 *
 * This event creates the `signupId`, which is used to stitch together
 * metrics tracking the e2e provisioning experience all the way into Jira.
 *
 * @param createAnalyticsEvent
 * @param signupId
 * @param productKey
 * @param edition
 */
export function trackSignupStarted(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    signupId: string,
    productKey?: string,
    edition?: string,
    isN2N?: boolean,
    domainType?: string
) {
    const ajcbGrsData = getGrsDataFromLocalStorage();
    const recoSessionId = ajcbGrsData?.recoSessionId;
    const recoEntityId = ajcbGrsData?.recoEntityId;

    // DATA FOUNDATIONS TRACKING
    createAnalyticsEvent({
        eventType: "track",
        action: "started",
        actionSubject: "signUp",
        containerId: "bxp-ui-form",
        attributes: {
            signupIdGenerated: signupId,
            productKey,
            edition,
            isN2N,
            domainType,
            recoSessionId,
            recoEntityId
        }
    }).fire();
}

/**
 * Tracks redirect to /checkemail page
 * @param AnalyticsEventType
 */
export function trackRedirectCheckEmail(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        eventName: "bxp.formCloudSignup.navigatedToCheckEmail"
    }).fire("signup");
}

/**
 * Tracks sign up with email button clicks
 * @param AnalyticsEventType
 */
export function trackSignUpWithEmail(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: "wac-try-signup"
    }).fire("signup");
}

/**
 * Tracks login link in error message clicks
 * @param AnalyticsEventType
 */
export function trackLoginInErrorMessage(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: "wac-signup-existing-user-signin"
    }).fire("signup");
}

/**
 * Tracks sitename submit
 * @param AnalyticsEventType
 */
export function trackSiteNameCreation(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        eventName: "reactSignUpFormSubmitButtonClicked",
        eventComponent: "button",
        event: "clicked"
    }).fire("signup");
}

/**
 * Tracks site picker site submitted
 * @param AnalyticsEventType
 * @param {string} targetCloudId The cloud id of the selected site
 * @param {Array.<{ product: string, edition: string }>} provisionedProducts The products and editions provisioned for the site
 * @param {string} signupUrl The URL to navigate to after the site is created
 * @param {string} [aaid] The AAID of the user
 */
export function trackSitePickerSubmit({
    createAnalyticsEvent,
    ...rest
}: {
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    targetCloudId: string;
    provisionedProducts: { product: string; edition?: string }[];
    signupUrl: string;
    aaid?: string;
    componentSource: string;
    signupSource?: string | null;
    hasHighestEditionStrategyWithPaidTargetEdition?: boolean;
}) {
    createAnalyticsEvent({
        eventName: "bxp.formCloudSignup.N2ESubmit",
        event: "clicked",
        eventComponent: "button",
        ...rest
    }).fire("signup");
}

/**
 * Tracks changed sitename submit from recommended sitename
 * @param AnalyticsEventType
 */
export function trackSiteNameChangedFromRecommendedSiteName(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    siteName: string,
    recommendedSiteName: string
) {
    createAnalyticsEvent({
        data: {
            siteName,
            recommendedSiteName
        },
        eventName: "reactSignUpFormSubmitButtonClicked",
        eventComponent: "button",
        event: "clicked"
    }).fire("signup");
}

/**
 * Tracks signup form load event
 * @param AnalyticsEventType
 */
export function trackVerifyEmailComponentLoad(
    createAnalyticsEvent: CreateUIAnalyticsEvent
) {
    createAnalyticsEvent({
        event: "loaded",
        eventName: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.PAGE_LOADED,
        eventType: "ui",
        eventComponent: "signUp",
        eventColumn: "signUp"
    }).fire("signup");
}

/**
 * Tracks Open in <Email Provider> button clicks
 * @param AnalyticsEventType
 */
export const trackOpenEmail = (
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    type: string
) => {
    let eventName = "";

    switch (type) {
        case "gmail":
            eventName =
                CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.OPEN_GMAIL_CLICKED;
            break;
        case "outlook":
            eventName =
                CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.OPEN_OUTLOOK_CLICKED;
            break;
    }

    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName
    }).fire("signup");
};

/**
 * Tracks Resend Email button clicks
 * @param AnalyticsEventType
 */
export function trackResendEmail(createAnalyticsEvent: CreateUIAnalyticsEvent) {
    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.RESEND_EMAIL_CLICKED
    }).fire("signup");
}

/**
 * Tracks Resend Email API success
 * @param AnalyticsEventType
 */
export const trackResendEmailSuccess = (
    createAnalyticsEvent: CreateUIAnalyticsEvent
) => {
    createAnalyticsEvent({
        eventName:
            CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.RESEND_EMAIL_API_SUCCESS
    }).fire("signup");
};

/**
 * Tracks Resend Email API failure
 * @param AnalyticsEventType
 */
export const trackResendEmailFailure = (
    createAnalyticsEvent: CreateUIAnalyticsEvent
) => {
    createAnalyticsEvent({
        eventName:
            CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.RESEND_EMAIL_API_FAILURE
    }).fire("signup");
};

/**
 * Tracks Contact Support button click
 * @param AnalyticsEventType
 */
export const trackContactSupport = (
    createAnalyticsEvent: CreateUIAnalyticsEvent
) => {
    createAnalyticsEvent({
        event: "clicked",
        eventComponent: "button",
        eventName: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.CONTACT_LINK_CLICKED
    }).fire("signup");
};

/**
 * Tracks fetches to /plan API
 * @returns {void}
 */
export const trackCrossFlowActivationPlanFetched = ({
    createAnalyticsEvent,
    cloudId,
    productKey
}: {
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    cloudId: string;
    productKey: string;
}) => {
    createAnalyticsEvent({
        actionSubject: "crossFlowActivationPlan",
        action: "fetched",
        attributes: {
            cloudId,
            productKey
        },
        eventType: "operational"
    }).fire();
};

/**
 * Tracks failures to fetches to /plan API
 * @returns {void}
 */
export const trackCrossFlowActivationPlanFailed = ({
    createAnalyticsEvent,
    cloudId,
    productKey,
    error
}: {
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    cloudId: string;
    productKey: string;
    error: unknown;
}) => {
    createAnalyticsEvent({
        actionSubject: "crossFlowActivationPlan",
        action: "failed",
        attributes: {
            cloudId,
            productKey,
            error
        },
        eventType: "operational"
    }).fire();
};
